import ConfigurationContext from '../../../core/providers/ConfigurationContext'
import { useContext } from 'react'
import { useBaseProcessor } from '../useBaseProcessor'
import { type UploadFormat } from '../UploadFormat'
import useApi from '../../../api/useApi'
import {
  type AimDataRow,
  type RawTelemetryData,
  type GpsReading,
  type Device,
  calculateDistance,
} from '@pitbrain/shared'

export const useAimProcessor = (): UploadFormat => {
  const bffConfig = useContext(ConfigurationContext)
  const api = useApi(bffConfig)
  const { getFirstCoordinate, processAndCreateSession } = useBaseProcessor()
  const { error: apiError } = api

  const parseAimToRawTelemetry = (results: AimDataRow[]): RawTelemetryData => {
    let totalDistance = 0
    const gps: GpsReading[] = results.map((p: AimDataRow, index, array) => {
      if (index > 0) {
        const prev = array[index - 1]
        totalDistance += calculateDistance(
          prev.latitude,
          prev.longitude,
          p.latitude,
          p.longitude
        )
      }

      return {
        latitude: p.latitude,
        longitude: p.longitude,
        altitude: p.altitude,
        speedMs: p.speed / 3.6,
        timestamp: new Date(p.date),
        distanceMeters: totalDistance,
      }
    })

    return {
      gps,
      gyroscope: undefined,
      accelerometer: undefined,
      recordingStartTime: gps[0]?.timestamp,
      device: {
        deviceName: 'AIM',
        deviceVersion: '1.0',
      },
    }
  }

  const processFile = async (file: File, sessionId: string): Promise<void> => {
    const results = await api.fileApi.convertXrkFile(file)
    const rawTelemetry = parseAimToRawTelemetry(results)
    const firstCoordinate = getFirstCoordinate(rawTelemetry)
    if (firstCoordinate == null) return

    const track = await api.trackApi.findTrack(firstCoordinate)
    const device: Device = { name: 'AIM', version: '1.0' }

    await processAndCreateSession(
      rawTelemetry,
      sessionId,
      track,
      { hasLeanAngle: false, hasVideo: false },
      file.name,
      device
    )
  }

  return {
    extensions: ['.XRK'],
    keepsFileAsSource: true,
    processFile,
    error: apiError,
  }
}
